import { useEffect, useState } from 'react';
import { trans } from '@lingoda/i18n';
import { Alert, Box, Button, Grid, LinearProgress, Loader } from '@lingoda/ui';
import { Form, type FormBag, type FormProps, withFormWrapper } from '@lingoda/forms';
import { withPayment } from '../../hoc';
import { useMaxPaymentMethods, usePaymentMethodType } from '../../hooks';
import { PAYMENT_SEPA, PAYMENT_SOFORT, PaymentMethodType, PaymentPurpose } from '../../models';
import { PaymentMethodForm } from '../PaymentMethodForm';
import { PaymentRequestButton } from '../PaymentRequestButton';
import { type FormValues, validate } from './schema';
import type { WithPaymentProps } from '../../hoc';

type OwnProps = {
    disabled?: boolean;
    currency: string;
    email?: string;
    chargeAmount?: number;
    isAddingPaymentMethod?: boolean;
    section: SectionName;
};

type Props = OwnProps & WithPaymentProps & FormProps<FormValues>;

const UpdatePaymentFormBase = ({
    chargeAmount,
    currency,
    values: { paymentMethod, country, name },
    disabled,
    validating,
    isSubmitting,
    isValid,
    setFieldValue,
    initPaymentRequest,
    resetForm,
    updatePaymentRequest,
    setErrorPayment,
    errorPayment,
    submitForm,
    paymentRequest,
    section,
}: Props) => {
    const { maxPaymentMethods, isLimitReached: maxPaymentMethodsReached } = useMaxPaymentMethods();
    const disableSubmit = disabled || validating || isSubmitting;
    const [validPaymentMethod, setValidPaymentMethod] = useState(false);
    const type = usePaymentMethodType(paymentMethod);
    const isPaymentRequest = type === PaymentMethodType.PaymentRequest;
    const paymentSummary = trans('test-charge-info', {}, 'public-product');
    const isDisabled = disableSubmit || !isValid || !validPaymentMethod;

    useEffect(() => {
        void setFieldValue('paymentMethodType', type);
    }, [type, setFieldValue]);

    useEffect(() => {
        if (isPaymentRequest && chargeAmount) {
            initPaymentRequest({
                currency,
                amount: chargeAmount,
                section,
                label: paymentSummary,
            });
        }
    }, [isPaymentRequest, section, currency, chargeAmount, paymentSummary, initPaymentRequest]);

    useEffect(() => {
        if (currency) {
            resetForm();
        }
    }, [currency, resetForm]);

    useEffect(() => {
        if (chargeAmount) {
            updatePaymentRequest({
                currency,
                amount: chargeAmount,
                label: paymentSummary,
            });
        }
    }, [chargeAmount, currency, paymentSummary, updatePaymentRequest]);

    if (!chargeAmount) {
        return <Loader minHeight={0} />;
    }

    return (
        <Form>
            <Grid container alignItems="center" spacing={2}>
                {maxPaymentMethodsReached && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            {trans(
                                'max-payment-methods-reached',
                                { limit: maxPaymentMethods },
                                'student-common+intl-icu',
                            )}
                        </Alert>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Box mb={2}>
                        <PaymentMethodForm
                            variant="new"
                            setError={setErrorPayment}
                            isValidCallback={setValidPaymentMethod}
                            currency={currency}
                            type={type}
                            errorMessage={errorPayment}
                            paymentMethod={paymentMethod}
                            country={country}
                            name={name}
                        />
                    </Box>
                </Grid>
                {isPaymentRequest && (
                    <Grid item xs={12}>
                        <PaymentRequestButton
                            onClick={submitForm}
                            paymentRequest={paymentRequest}
                            disabled={isDisabled || maxPaymentMethodsReached}
                        />
                    </Grid>
                )}

                {!isPaymentRequest && (
                    <Grid item xs={12}>
                        <Button
                            color="primary"
                            variant="contained"
                            type="submit"
                            disabled={isDisabled || maxPaymentMethodsReached}
                        >
                            {trans('add-payment-method', {}, 'public-courses-summary')}
                        </Button>
                    </Grid>
                )}

                {disableSubmit && (
                    <Grid item xs={12}>
                        <LinearProgress />
                    </Grid>
                )}

                {(paymentMethod === PAYMENT_SEPA || paymentMethod === PAYMENT_SOFORT) && (
                    <Grid item xs={12}>
                        {trans('sepa-payment-description', {}, 'public-payment')}
                    </Grid>
                )}
            </Grid>
        </Form>
    );
};

export const UpdatePaymentForm = withPayment<OwnProps>(
    withFormWrapper<Props, FormValues>({
        validate,
        validateOnMount: true,
        mapPropsToValues: () =>
            ({
                paymentMethod: undefined,
                name: undefined,
                country: undefined,
                paymentMethodType: undefined,
            }) as FormValues,
        handleSubmit: (
            values: FormValues,
            { props: { submitPayment, email, section }, setSubmitting }: FormBag<Props, FormValues>,
        ) => {
            if (values.paymentMethodType === PaymentMethodType.PaymentRequest) {
                setSubmitting(false);

                return;
            }

            setSubmitting(true);

            submitPayment(
                {
                    name: values.name,
                    email,
                    section,
                    country: values.country,
                    paymentMethod: {
                        type: values.paymentMethodType,
                        value: values.paymentMethod,
                    },
                },
                {
                    ...values,
                },
                {
                    paymentPurpose: PaymentPurpose.paymentMethod,
                },
            );

            setSubmitting(false);
        },
    })(UpdatePaymentFormBase),
);
